<template>
  <BlBackdrop title="Cancel appointment" :footer="false">
    <div
      class="overflow-y-auto px-8 mb-4"
      :style="{ width: '40rem', maxHeight: 'calc(100vh - 14rem)' }"
    >
      <div v-if="modalLoading" class="h-64 grid place-items-center">
        <div class="blum-loader"></div>
      </div>
      <div v-else-if="appointment && praCancelOpened">
        <div v-if="appointment.client" class="mb-4">
          <BlTextBody2 text="This appointment for" />
          <BlTextBody2 :text="appointment.client.name" class="font-bold" />
          <BlTextBody2
            text="is part of the recurring appointment. Would you like to cancel just this one, or this and all following appointments (totaling"
          />
          <BlTextBody2
            :text="`${totalCancellation} appointments)?`"
            class="font-bold"
          />
        </div>
        <div class="grid grid-cols-2 gap-2">
          <div
            v-for="opt in cancelOptionOptions"
            :key="opt.value"
            class="h-22 flex items-center justify-center text-center rounded border cursor-pointer"
            :class="{
              'border-light-purple': cancelOptionInput === opt.value
            }"
            @click="cancelOptionInput = opt.value"
          >
            <BlTextSubtitle3
              :text="opt.label"
              :class="[
                cancelOptionInput === opt.value
                  ? 'text-light-purple'
                  : 'text-slate-700'
              ]"
            />
          </div>
        </div>
      </div>
      <div v-else-if="appointment && !praCancelOpened">
        <div v-if="appointment.client" class="mb-4">
          <div v-if="cancelOptionInput === 'current'">
            <BlTextBody2 text="You are about to cancel the appointment for" />
            <BlTextBody2 :text="appointment.client.name" class="font-bold" />
            <BlTextBody2 text="with the following details:" />
          </div>
          <div v-if="cancelOptionInput === 'current_and_following'">
            <BlTextBody2 text="To proceed with the cancellation for" />
            <BlTextBody2
              :text="`${appointment.client.name},`"
              class="font-bold"
            />
            <BlTextBody2
              text="please provide the cancellation details. These will apply to all"
            />
            <BlTextBody2 :text="totalCancellation" />
            <BlTextBody2 text="appointments." />
          </div>
        </div>

        <div
          v-if="cancelOptionInput === 'current'"
          class="flex items-center gap-2 mb-4"
        >
          <div class="w-25 flex-shrink-0 rounded shadow-md">
            <div
              class="h-7 flex items-center justify-center rounded-t bg-orange-200"
            >
              <BlTextBody2 text="Existing" class="text-orange-700" />
            </div>
            <div
              class="h-18 flex flex-col items-center justify-center rounded-b bg-pure-white"
            >
              <BlTextTitle4
                :text="displayDate({ date: appointment.date, format: 'DD' })"
                class="text-orange-700"
              />
              <BlTextBody2
                :text="displayDate({ date: appointment.date, format: 'MMMM' })"
                class="text-orange-600"
              />
            </div>
          </div>
          <div class="flex flex-col gap-1 px-2">
            <div
              class="max-w-100 flex items-center gap-2"
              :text="
                (appointment.user && appointment.user.name) ||
                  'No therapist assigned'
              "
            >
              <BlIcon icon="user" color="slate-6" class="h-4 w-4" />
              <BlTextBody2
                :text="
                  (appointment.user && appointment.user.name) ||
                    'No therapist assigned'
                "
                class="truncate"
              />
            </div>
            <div class="flex items-center gap-2">
              <BlIcon icon="clock-outline" color="slate-6" class="h-4 w-4" />
              <BlTextBody2
                :text="
                  `${appointment.start_time_string} - ${appointment.end_time_string}`
                "
              />
            </div>
            <div v-if="enableBranch" class="max-w-100 flex items-center gap-2">
              <BlIcon icon="location" color="slate-6" class="h-4 w-4" />
              <BlTextBody2
                :text="
                  appointment.room.branch
                    ? appointment.room.branch.name
                    : 'No branch'
                "
                class="truncate"
              />
            </div>
            <div class="max-w-100 flex items-center gap-2">
              <BlIcon icon="door" color="slate-6" class="h-4 w-4" />
              <BlTextBody2 :text="appointment.room.name" class="truncate" />
            </div>
          </div>
        </div>

        <div
          v-if="cancelOptionInput === 'current_and_following'"
          class="flex items-center gap-2 mb-4"
        >
          <div class="w-25 flex-shrink-0 rounded shadow-md">
            <div
              class="h-7 flex items-center justify-center rounded-t bg-orange-200"
            >
              <BlTextBody2 text="Start" class="text-orange-700" />
            </div>
            <div
              class="h-18 flex flex-col items-center justify-center rounded-b bg-pure-white"
            >
              <BlTextTitle4
                :text="displayDate({ date: appointment.date, format: 'DD' })"
                class="text-orange-700"
              />
              <BlTextBody2
                :text="displayDate({ date: appointment.date, format: 'MMMM' })"
                class="text-orange-600"
              />
            </div>
          </div>
          <div class="flex flex-col items-center justify-center w-full">
            <div class="flex items-center gap-2">
              <BlTextBody2 :text="`${totalCancellation} appointments`" />
            </div>
            <div class="flex items-center gap-3">
              <div
                class="h-3 w-3 flex-shrink-0 rounded-full bg-slate-500"
              ></div>
              <div
                v-for="n in 13"
                :key="n"
                class="h-0.5 w-3 flex-shrink-0 bg-slate-500"
              ></div>
              <div
                class="h-3 w-3 flex-shrink-0 rounded-full bg-slate-500"
              ></div>
            </div>
            <div class="h-5.5 flex-shrink-0"></div>
          </div>
          <div class="w-25 flex-shrink-0 rounded shadow-md">
            <div
              class="h-7 flex items-center justify-center rounded-t bg-orange-200"
            >
              <BlTextBody2 text="End" class="text-orange-700" />
            </div>
            <div
              class="h-18 flex flex-col items-center justify-center rounded-b bg-pure-white"
            >
              <BlTextTitle4
                :text="
                  displayDate({
                    date: appointment.last_upcoming_appointment_date,
                    format: 'DD'
                  })
                "
                class="text-orange-700"
              />
              <BlTextBody2
                :text="
                  displayDate({
                    date: appointment.last_upcoming_appointment_date,
                    format: 'MMMM'
                  })
                "
                class="text-orange-600"
              />
            </div>
          </div>
        </div>

        <div class="mb-4 flex flex-col gap-4">
          <div class="flex flex-col gap-1">
            <label>
              <BlTextBody2 text="Select who is requesting the cancellation:" />
            </label>
            <div class="grid grid-cols-3 gap-2">
              <div
                v-for="item in cancellationRequesterTypeOptions"
                :key="item.value"
                class="h-15 flex items-center justify-center text-center rounded border cursor-pointer"
                :class="{
                  'border-light-purple':
                    cancellationRequesterTypeInput === item.value
                }"
                @click="cancellationRequesterTypeInput = item.value"
              >
                <BlTextSubtitle3
                  :text="item.label"
                  :class="[
                    cancellationRequesterTypeInput === item.value
                      ? 'text-light-purple'
                      : 'text-slate-700'
                  ]"
                />
              </div>
            </div>
          </div>
          <div class="flex flex-col gap-1">
            <label>
              <BlTextBody2
                :text="
                  `Do you want to apply a deduction of ${currentSlotCount} credit(s) for this cancellation?`
                "
              />
            </label>
            <div class="grid grid-cols-2 gap-2">
              <div
                v-for="item in subtractSessionCreditOptions"
                :key="item.value"
                class="h-15 flex items-center justify-center text-center rounded border cursor-pointer"
                :class="{
                  'border-light-purple':
                    subtractSessionCreditInput === item.value
                }"
                @click="subtractSessionCreditInput = item.value"
              >
                <BlTextSubtitle3
                  :text="item.label"
                  :class="[
                    subtractSessionCreditInput === item.value
                      ? 'text-light-purple'
                      : 'text-slate-700'
                  ]"
                />
              </div>
            </div>
          </div>
          <div class="flex flex-col gap-1">
            <BlSelectBox
              label="Please select reason for the cancellation."
              asterisk
              v-model="cancellationReasonInput"
              placeholder="Select cancellation reason"
              :options="
                centerReasonSettings.map(i => ({
                  value: i.formatted_name,
                  label: i.name
                }))
              "
              drop="up"
              @change="cancellationReasonInput = $event"
            />
            <BlInputText
              v-if="cancellationReasonInput === defaultReason"
              :input="otherCancellationReasonInput"
              @changed="inputHandler"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="flex items-center justify-end gap-4 px-8 pb-8">
      <BlInputButton
        v-if="praCancelOpened || totalCancellation === 1"
        type="plain"
        label="Cancel"
        @clicked="$emit('close-modal')"
      />
      <BlInputButton
        v-if="!praCancelOpened && totalCancellation > 1"
        type="plain"
        label="Back"
        @clicked="praCancelOpened = true"
      />
      <div v-if="!modalLoading">
        <BlInputButton
          v-if="praCancelOpened"
          label="Next"
          :loading="submitLoading"
          @clicked="onNext"
        />
        <BlInputButton
          v-else
          label="Confirm cancellation"
          :disabled="disabledSubmit"
          :loading="submitLoading"
          @clicked="onCancelAppointment"
        />
      </div>
    </div>
  </BlBackdrop>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { displayDate } from "@/plugins/helper.js";

import BlBackdrop from "../../BlBackdrop.vue";
import BlTextTitle4 from "../../BlTextTitle4.vue";
import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlIcon from "../../BlIcon.vue";
import BlInputText from "../../BlInputText.vue";
import BlSelectBox from "../../BlSelectBox.vue";
import BlInputButton from "../../BlInputButton.vue";

export default {
  name: "CancelAppointmentModal",
  components: {
    BlBackdrop,
    BlTextTitle4,
    BlTextSubtitle3,
    BlTextBody2,
    BlIcon,
    BlInputText,
    BlSelectBox,
    BlInputButton
  },
  props: {
    appointmentId: { type: Number, required: true }
  },
  data() {
    return {
      praCancelOpened: false,
      modalLoading: false,
      submitLoading: false,
      //
      cancelOptionInput: "current", // current | current_and_following
      cancellationRequesterTypeInput: "client",
      cancellationRequesterTypeOptions: [
        { value: "client", label: "Client" },
        { value: "therapist", label: "Therapist" },
        { value: "clinic_or_admin", label: "Admin/clinic" }
      ],
      subtractSessionCreditInput: true, // false | true
      subtractSessionCreditOptions: [
        { value: true, label: "Yes" },
        { value: false, label: "No" }
      ],
      cancellationReasonInput: "",
      otherCancellationReasonInput: {
        placeholder: "Provide a reason for the cancellation.",
        asterisk: true,
        textarea: true,
        model: ""
      }
    };
  },
  computed: {
    ...mapGetters([
      "currentUser",
      "currentCenter",
      "centerReasonSettings",
      "appointment"
    ]),
    enableBranch() {
      return this.currentCenter.enable_branch;
    },
    defaultReason() {
      const reason = this.centerReasonSettings.find(i => i.is_default);
      return reason ? reason.formatted_name : "other";
    },
    totalCancellation() {
      if (!this.appointment) return 0;
      return this.appointment.remaining_appointments_count + 1;
    },
    cancelOptionOptions() {
      return [
        { value: "current", label: "Cancel only this appointment" },
        {
          value: "current_and_following",
          label: `Cancel this and all following appointments`
        }
      ];
    },
    currentSlotCount() {
      if (!this.appointment) return 0;
      const count = this.getSlotCount(
        this.appointment.start_time_string,
        this.appointment.end_time_string
      );
      if (this.cancelOptionInput === "current_and_following") {
        return this.appointment.total_upcoming_credits;
      }
      return count;
    },
    disabledSubmit() {
      if (!this.cancellationReasonInput) return true;
      if (this.cancellationReasonInput === this.defaultReason) {
        if (!this.otherCancellationReasonInput.model) return true;
      }
      return false;
    }
  },
  watch: {},
  created() {
    this.getCenterReasonSettings();
  },
  async mounted() {
    this.modalLoading = true;
    const id = this.appointmentId;
    const { success, data } = await this.getAppointment({ id });
    this.modalLoading = false;
    if (!success) {
      this.$emit("close-modal");
    }
    if (data.remaining_appointments_count) {
      this.praCancelOpened = true;
    }
  },
  methods: {
    ...mapActions([
      "getCenterReasonSettings",
      "getAppointment",
      "cancelAppointment"
    ]),
    displayDate,
    inputHandler(payload) {
      payload.input.model = payload.value;
    },
    getSlotCount(start, end) {
      const s = start.split(":").map(i => Number(i));
      const e = end.split(":").map(i => Number(i));
      let slots = 0;
      slots += (e[0] - s[0]) * 2;
      if (s[1] === 30) slots -= 1;
      if (e[1] === 30) slots += 1;
      return slots;
    },
    onNext() {
      this.praCancelOpened = false;
    },
    async onCancelAppointment() {
      let requesterType;
      let requesterId;
      const cancelledBy = this.cancellationRequesterTypeInput;
      switch (cancelledBy) {
        case "client":
          requesterType = "Client";
          const clientId = this.appointment.client.id;
          requesterId = clientId;
          break;
        case "therapist":
          requesterType = "User";
          const userId =
            (this.appointment.user && this.appointment.user.id) ||
            this.currentUser.id;
          requesterId = userId;
          break;
        case "clinic_or_admin":
          requesterType = "User";
          requesterId = this.currentUser.id;
          break;
        default:
          requesterType = "";
          requesterId = "";
          break;
      }

      const data = {
        id: this.appointment.id,
        slot_count: this.currentSlotCount,
        client_name: this.appointment.client.name,
        params: {
          cancel_option: this.cancelOptionInput,
          cancellation_requester_type: requesterType,
          cancellation_requester_id: requesterId,
          cancelled_by: cancelledBy,
          subtract_session_credit: this.subtractSessionCreditInput,
          cancellation_reason: this.cancellationReasonInput,
          other_cancellation_reason: ""
        }
      };

      if (this.cancellationReasonInput === this.defaultReason) {
        data.params.other_cancellation_reason = this.otherCancellationReasonInput.model;
      }

      this.submitLoading = true;
      const { success } = await this.cancelAppointment(data);
      if (!success) {
        this.submitLoading = false;
        return;
      }
      setTimeout(() => {
        this.submitLoading = false;
        this.$emit("after-commit");
        this.$emit("close-modal");
      }, 1000);
    }
  }
};
</script>

<style scoped lang="scss">
.bl-input-full-indicator {
  &::-webkit-calendar-picker-indicator {
    position: absolute;
    opacity: 0;
    width: auto;
    height: auto;
    cursor: pointer;
    bottom: 0;
    left: 0;
    right: 0;
    top: 0;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/appointments/modal/CancelAppointmentModal.vue